import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Undo2, TrendingUp, CheckSquare, FileText, DollarSign, History } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface CaptureHistoryItem {
  id: string;
  entry_type: string;
  entry_id: string | null;
  data: any;
  created_at: string;
}

interface CaptureHistorySidebarProps {
  onUpdate: () => void;
}

export const CaptureHistorySidebar = ({ onUpdate }: CaptureHistorySidebarProps) => {
  const { toast } = useToast();
  const [history, setHistory] = useState<CaptureHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('capture-history-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'capture_history'
        },
        () => {
          fetchHistory();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchHistory = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('capture_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUndo = async (item: CaptureHistoryItem) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Delete the original entry
      const tableName = getTableName(item.entry_type);
      
      if (tableName && item.entry_id) {
        const { error } = await supabase
          .from(tableName as any)
          .delete()
          .eq('id', item.entry_id)
          .eq('user_id', user.id);

        if (error) throw error;
      }

      // Delete the history entry
      const { error: historyError } = await supabase
        .from('capture_history')
        .delete()
        .eq('id', item.id)
        .eq('user_id', user.id);

      if (historyError) throw historyError;

      toast({
        title: "Entry Undone",
        description: `${getTypeLabel(item.entry_type)} has been removed.`,
      });

      onUpdate();
    } catch (error) {
      console.error('Undo error:', error);
      toast({
        title: "Undo Failed",
        description: "Failed to undo the entry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getTableName = (type: string): string | null => {
    switch (type) {
      case 'lead': return 'sales_leads';
      case 'task': return 'tasks';
      case 'invoice': return 'invoices';
      case 'income': return 'finance_transactions';
      case 'expense': return 'finance_transactions';
      default: return null;
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'lead': return 'Lead';
      case 'task': return 'Task';
      case 'invoice': return 'Invoice';
      case 'income': return 'Income';
      case 'expense': return 'Expense';
      default: return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'lead': return <TrendingUp className="h-4 w-4" />;
      case 'task': return <CheckSquare className="h-4 w-4" />;
      case 'invoice': return <FileText className="h-4 w-4" />;
      case 'income': return <DollarSign className="h-4 w-4" />;
      case 'expense': return <DollarSign className="h-4 w-4" />;
      default: return <History className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'lead': return 'bg-primary/10 text-primary';
      case 'task': return 'bg-secondary/10 text-secondary-foreground';
      case 'invoice': return 'bg-accent/10 text-accent-foreground';
      case 'income': return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'expense': return 'bg-red-500/10 text-red-700 dark:text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getDisplayText = (item: CaptureHistoryItem): string => {
    const { entry_type, data } = item;
    
    switch (entry_type) {
      case 'lead':
        return data.company || data.client || 'New Lead';
      case 'task':
        return data.title || 'New Task';
      case 'invoice':
        return `Invoice ${data.client ? 'for ' + data.client : ''}`;
      case 'income':
        return `₹${data.amount} ${data.category ? '(' + data.category + ')' : ''}`;
      case 'expense':
        return `₹${data.amount} ${data.category ? '(' + data.category + ')' : ''}`;
      default:
        return 'Captured Entry';
    }
  };

  if (loading && history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
        <History className="h-12 w-12 mb-2 opacity-20" />
        <p className="text-sm">Loading history...</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
        <History className="h-12 w-12 mb-2 opacity-20" />
        <p className="text-sm">No capture history yet</p>
        <p className="text-xs mt-1">Your last 10 entries will appear here</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <h3 className="font-semibold flex items-center gap-2">
          <History className="h-4 w-4" />
          Recent Captures
        </h3>
        <p className="text-xs text-muted-foreground mt-1">Last 10 entries</p>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {history.map((item) => (
            <div key={item.id} className="group relative">
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <div className={`p-2 rounded-md ${getTypeColor(item.entry_type)}`}>
                  {getTypeIcon(item.entry_type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">
                      {getTypeLabel(item.entry_type)}
                    </Badge>
                  </div>
                  
                  <p className="text-sm font-medium truncate">
                    {getDisplayText(item)}
                  </p>
                  
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                  </p>
                </div>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleUndo(item)}
                  disabled={loading}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Undo2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};